/**
 * PD-15071 — how much has actually been paid against an invoice.
 *
 * Moved out of InvoiceBalanceService so the invoice totals recalculation can
 * use the same rules. Those two disagreed: the totals recalculation wrote the
 * whole invoice total into the outstanding balance, so editing a line on a
 * settled invoice made it read as fully unpaid and ask the customer for money
 * already collected. Both now net payments the same way, because there is one
 * definition of "paid".
 *
 * The rules come from the legacy supply-mover-api and are preserved exactly.
 * Transactions are grouped into THREADS by their origin transaction — an
 * authorization, the capture that settles it, a later refund, a void of that
 * refund all belong to one thread — and each thread is netted on its own
 * before the threads are summed. That is what lets a partial refund reduce a
 * payment without erasing it, and a void of a refund restore it.
 *
 * Amounts are the amount APPLIED to this invoice (the link row), not the
 * amount charged to the card, so an invoice paid as part of a larger payment
 * counts only its own share.
 */

import { InvoiceStatus } from '../enums/invoice-status.enum';
import { isPaymentNotCollectedStatus } from './payment-failure-status';

export const INVOICE_PAYMENT_TYPE = {
	AUTHORIZATION: 'Authorization',
	CAPTURE: 'Capture',
	SALE: 'Sale',
	VOID: 'Void',
	REFUND: 'Refund',
} as const;

export const INVOICE_PAYMENT_STATUS_FAILED = 'Failed';

export const INVOICE_FEE_UNIT = {
	PERCENT: '%',
	FLAT_RATE: '$',
} as const;

export interface InvoicePaymentForCalculationModel {
	id: number;
	paymentType: string;
	paymentStatus: string;
	/** Gross amount charged at the gateway, fee included. */
	paymentAmount: number;
	paymentFeeUnit?: string | null;
	paymentFeeAmount?: number | null;
	paymentTransactionParentId?: number | null;
	paymentTransactionOriginId?: number | null;
	/** The share of that payment applied to THIS invoice. */
	invoicePaymentAmount: number;
}

/**
 * Strip the processing fee back out of a gross charge, so a parent
 * authorization can be compared against the fee-exclusive figure applied to
 * the invoice.
 */
function netOfFees(
	paymentAmount: number,
	feeUnit?: string | null,
	feeAmount?: number | null,
): number {
	if (paymentAmount === 0) {
		return 0;
	}

	if (
		feeUnit === INVOICE_FEE_UNIT.PERCENT &&
		feeAmount != null &&
		feeAmount !== 0
	) {
		return paymentAmount / (1 + feeAmount / 100);
	}

	if (
		feeUnit === INVOICE_FEE_UNIT.FLAT_RATE &&
		feeAmount != null &&
		feeAmount !== 0
	) {
		return paymentAmount - feeAmount;
	}

	return paymentAmount;
}

/**
 * Net one thread: captures and sales add, refunds subtract, and a void
 * reverses whichever it was applied to.
 */
function calculateThreadNetAmount(
	thread: Array<InvoicePaymentForCalculationModel>,
): number {
	let threadNetAmount = 0;

	const byId = new Map<number, InvoicePaymentForCalculationModel>();
	for (const transaction of thread) {
		byId.set(transaction.id, transaction);
	}

	for (const current of thread) {
		// A failed transaction never moved money — and neither did a decline,
		// an error, or a hold-for-review the processor has not approved
		// (PD-15305: the single 'Failed' literal let those net in as paid).
		if (isPaymentNotCollectedStatus(current.paymentStatus)) {
			continue;
		}

		const parent =
			current.paymentTransactionParentId != null
				? byId.get(current.paymentTransactionParentId)
				: undefined;
		const applied = current.invoicePaymentAmount;

		if (
			current.paymentType === INVOICE_PAYMENT_TYPE.CAPTURE ||
			current.paymentType === INVOICE_PAYMENT_TYPE.SALE
		) {
			threadNetAmount += applied;

			// A capture settling something that is NOT an authorization is
			// replacing an earlier charge rather than adding to it, so the
			// earlier one comes back off.
			if (
				parent != null &&
				parent.paymentType !== INVOICE_PAYMENT_TYPE.AUTHORIZATION
			) {
				threadNetAmount -= netOfFees(
					parent.paymentAmount,
					parent.paymentFeeUnit,
					parent.paymentFeeAmount,
				);
			}
		}

		if (current.paymentType === INVOICE_PAYMENT_TYPE.VOID && parent != null) {
			// Voiding a refund puts the money back; voiding a capture takes it away.
			if (parent.paymentType === INVOICE_PAYMENT_TYPE.REFUND) {
				threadNetAmount += applied;
			} else {
				threadNetAmount -= applied;
			}
		}

		if (current.paymentType === INVOICE_PAYMENT_TYPE.REFUND) {
			threadNetAmount -= applied;
		}
	}

	return threadNetAmount;
}

/**
 * Total applied to the invoice across every payment thread. Authorizations
 * contribute nothing on their own — a hold is not a payment — which is why an
 * invoice awaiting delivery still reads as owing.
 */
export function calculateInvoiceAmountPaid(
	transactions: Array<InvoicePaymentForCalculationModel>,
): number {
	const threads = new Map<number, Array<InvoicePaymentForCalculationModel>>();

	for (const transaction of transactions) {
		const originId = transaction.paymentTransactionOriginId ?? transaction.id;
		const thread = threads.get(originId);

		if (thread == null) {
			threads.set(originId, [transaction]);
		} else {
			thread.push(transaction);
		}
	}

	let total = 0;
	for (const thread of threads.values()) {
		total += calculateThreadNetAmount(thread);
	}

	return total;
}

/**
 * What the invoice still owes: its total less what has been paid, never
 * negative. An overpayment is money on the customer's account, not a negative
 * invoice.
 *
 * PD-15441 — a CREDIT MEMO (negative total) mirrors the rule: its balance is
 * what is still owed TO the customer, tracked as a negative number (the
 * PD-15026 convention) and floored at zero from the other side. Applying part
 * of the memo against invoices records a negative application, which walks
 * the balance from `total` up toward zero: a -$1000 memo with -$400 applied
 * has -$600 of credit left. Without this branch the invoice-side
 * `max(0, ...)` would zero the memo's remaining credit on the first partial
 * application.
 */
export function calculateInvoiceOutstandingBalance(
	invoiceTotal: number,
	transactions: Array<InvoicePaymentForCalculationModel>,
): number {
	const amountPaid = calculateInvoiceAmountPaid(transactions);

	if (invoiceTotal < 0) {
		return Math.min(0, invoiceTotal - amountPaid);
	}

	return Math.max(0, invoiceTotal - amountPaid);
}

/**
 * The payment status shown on an invoice or credit memo.
 *
 * PD-15026 — credit memos need their own vocabulary. A credit memo carries a
 * NEGATIVE outstanding balance (we owe the customer), and the invoice rule
 * `balance > 0 ? 'Outstanding' : 'Paid in Full'` therefore labelled every
 * unrefunded credit "Paid in Full" — the exact opposite of the truth, on the
 * one document where getting it wrong means the customer never gets paid.
 *
 * A credit is OUTSTANDING while we still owe it and APPLIED once the balance
 * has been cleared, whether by refund or by offsetting an invoice.
 *
 * A CANCELLED document reports "Cancelled": its balance is whatever it
 * happened to be when it was cancelled, so both readings the binary offers
 * ("Paid in Full" for a zeroed balance, "Outstanding" for a leftover one)
 * misstate a document nobody owes anything on — and the invoice lists have
 * no separate document-status column, so an empty value left cancelled
 * invoices indistinguishable from live ones (owner feedback on PD-15224).
 */
export function deriveInvoicePaymentStatus(
	outstandingBalance: number | null,
	isCreditMemo: boolean,
	invoiceStatus?: string | null,
): string | null {
	if (invoiceStatus === InvoiceStatus.CANCELLED) {
		return 'Cancelled';
	}

	if (outstandingBalance == null) {
		return null;
	}

	if (isCreditMemo) {
		// PD-15441 — read the MAGNITUDE, not the sign. The two producers of a
		// credit memo's remaining balance disagree: the ERP sync stores it
		// POSITIVE (HF Foods) while SM-issued memos store it NEGATIVE
		// (PD-15026). Testing `< 0` therefore called every ERP-synced RMA
		// "Credit Applied" while the customer was still owed the money — the
		// same mistake the invoice rule made before PD-15026, one convention
		// further along. Any non-zero balance means credit is still owed.
		return creditMemoRemainingCredit(outstandingBalance) > 0
			? 'Credit Outstanding'
			: 'Credit Applied';
	}

	return outstandingBalance > 0 ? 'Outstanding' : 'Paid in Full';
}

/**
 * PD-15441 — the credit still available on a credit memo, as a POSITIVE
 * magnitude, whichever convention stored it.
 *
 * The ERP sync writes a memo's remaining balance positive (HF Foods'
 * Workday feed) while an SM-issued memo writes it negative (PD-15026). Both
 * mean the same thing — "this much credit is still owed to the customer" —
 * so every display and comparison takes the magnitude rather than picking a
 * side. Accepts the raw string the DTOs carry as well as a parsed number.
 *
 * Returns 0 for null/unparseable input: an unknown balance is not credit we
 * can offer to spend.
 */
export function creditMemoRemainingCredit(
	outstandingBalance: number | string | null | undefined,
): number {
	if (outstandingBalance == null) {
		return 0;
	}
	const parsed =
		typeof outstandingBalance === 'string'
			? parseFloat(outstandingBalance)
			: outstandingBalance;
	return Number.isFinite(parsed) ? Math.abs(parsed) : 0;
}

/**
 * How much of a credit memo has been used, as a display state.
 *
 * The customer-facing counterpart to `deriveInvoicePaymentStatus`'s
 * Outstanding / Paid in Full split. A credit memo's good state is the
 * INVERSE of an invoice's: credit still available is what the customer
 * wants, so `available` is the reassuring state and `used` the spent one.
 *
 *   available — none of it applied yet
 *   partial   — some applied, some credit left
 *   used      — fully applied, nothing left to spend
 *
 * Both figures are read as magnitudes (see `creditMemoRemainingCredit`).
 * A memo with no recorded balance falls back to its total, i.e. fully
 * available — matching what the payable-invoice list does with a NULL
 * balance.
 */
export function deriveCreditMemoApplicationStatus(
	outstandingBalance: number | string | null | undefined,
	total: number | string | null | undefined,
): 'available' | 'partial' | 'used' {
	const issued = creditMemoRemainingCredit(total);
	const remaining =
		outstandingBalance == null
			? issued
			: creditMemoRemainingCredit(outstandingBalance);

	// Cent tolerance on both edges: the balance is stored to two decimals but
	// arrives through float parsing, and a memo one cent from depleted should
	// not read as still partly available.
	if (remaining <= 0.005) {
		return 'used';
	}
	if (issued > 0 && remaining < issued - 0.005) {
		return 'partial';
	}
	return 'available';
}
